import { useSyncExternalStore } from 'react';
import { api, streamPost, unwrap, type Schemas } from '../../api/client';
import { useApiQuery } from '../../api/hooks';
import { BRAND } from '../../lib/brand';
import { helpLocale } from './data';

// Xone Assistant in the help panel (APP-DESIGN.md section 11, ADR 0047): the conversation, kept in
// this browser tab while the panel is closed and reopened, and started fresh after sign-out or 24
// hours (11.2). The answers come from POST /v1/assistant/ask as server-sent events.

export type AssistantMode = 'help' | 'books';
export type AssistantUsage = Schemas['AssistantUsage'];

export interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
  /** An answer still arriving. */
  pending?: boolean;
  /** What a books tool is doing right now ("Looking at overdue invoices"). */
  activity?: string | null;
  messageId?: string;
  articles?: { id: string; title: string; url: string }[];
  noAnswer?: boolean;
  taxNote?: boolean;
  feedback?: 'solved' | 'handover';
  error?: { code: string; message: string };
}

export interface Conversation {
  /** Whose and where: a conversation never follows someone into another company or account. */
  owner: string;
  mode: AssistantMode;
  id: string | null;
  startedAt: number;
  messages: ChatMessage[];
}

const KEY = 'xone.assistant';
const MAX_AGE = 24 * 60 * 60 * 1000;

let current: Conversation | null = load();
const listeners = new Set<() => void>();

function load(): Conversation | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const c = JSON.parse(raw) as Conversation;
    if (Date.now() - c.startedAt > MAX_AGE) { sessionStorage.removeItem(KEY); return null; }
    // A reload while an answer was arriving: that answer is gone.
    return { ...c, messages: c.messages.map((m) => (m.pending ? { ...m, pending: false, activity: null, error: m.error ?? { code: 'interrupted', message: 'This answer was interrupted.' } } : m)) };
  } catch {
    return null;
  }
}
function save(c: Conversation | null) {
  current = c;
  try {
    if (c) sessionStorage.setItem(KEY, JSON.stringify(c));
    else sessionStorage.removeItem(KEY);
  } catch { /* storage blocked: the conversation lasts as long as the page */ }
  for (const l of listeners) l();
}

/** Forget the conversation (sign-out). */
export const clearAssistant = () => save(null);

/** The conversation for this person in this company, or a fresh one. */
export function useConversation(owner: string): Conversation | null {
  const c = useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => current);
  if (c && (c.owner !== owner || Date.now() - c.startedAt > MAX_AGE)) return null;
  return c;
}

const update = (fn: (c: Conversation) => Conversation) => { if (current) save(fn(current)); };
const updateLast = (fn: (m: ChatMessage) => ChatMessage) => update((c) => ({ ...c, messages: [...c.messages.slice(0, -1), fn(c.messages[c.messages.length - 1]!)] }));

/** Starts over, in a mode. */
export function startConversation(owner: string, mode: AssistantMode) {
  save({ owner, mode, id: null, startedAt: Date.now(), messages: [] });
}

/** Asks a question in the current conversation and streams the answer into it. */
export async function ask(owner: string, mode: AssistantMode, question: string, page: string): Promise<void> {
  if (!current || current.owner !== owner || current.mode !== mode) startConversation(owner, mode);
  const conversationId = current!.id;
  update((c) => ({ ...c, messages: [...c.messages, { role: 'user', text: question }, { role: 'assistant', text: '', pending: true, activity: null }] }));
  try {
    await streamPost('/v1/assistant/ask', { question, mode, conversation_id: conversationId, locale: helpLocale(), brand: BRAND.id, page }, (event, data) => {
      const d = data as Record<string, unknown>;
      if (event === 'meta') update((c) => ({ ...c, id: String(d.conversation_id) }));
      else if (event === 'tool') updateLast((m) => ({ ...m, activity: String(d.label ?? 'Reading your books') }));
      else if (event === 'text') updateLast((m) => ({ ...m, text: m.text + String(d.text ?? ''), activity: null }));
      else if (event === 'done') {
        updateLast((m) => ({
          ...m, pending: false, activity: null, text: String(d.text ?? m.text), messageId: String(d.message_id),
          articles: (d.articles as ChatMessage['articles']) ?? [], noAnswer: d.no_answer === true, taxNote: d.tax_note === true,
        }));
      } else if (event === 'error') {
        updateLast((m) => ({ ...m, pending: false, activity: null, error: { code: String(d.code), message: String(d.message) } }));
      }
    });
  } catch (e) {
    const err = e as { code?: string; message?: string };
    updateLast((m) => ({ ...m, pending: false, activity: null, error: { code: err.code ?? 'assistant_unavailable', message: err.message ?? "The assistant isn't available right now." } }));
  }
  // A stream that ended without "done" or "error" (the connection dropped).
  if (current?.messages.at(-1)?.pending) updateLast((m) => ({ ...m, pending: false, activity: null, error: { code: 'interrupted', message: "The assistant isn't available right now." } }));
}

export async function giveFeedback(messageId: string, feedback: 'solved' | 'handover') {
  update((c) => ({ ...c, messages: c.messages.map((m) => (m.messageId === messageId ? { ...m, feedback } : m)) }));
  await unwrap(api.POST('/v1/assistant/messages/{id}/feedback', { params: { path: { id: messageId } }, body: { feedback } })).catch(() => {});
}

export function useAssistantUsage() {
  return useApiQuery(['support', 'assistant-usage'], async () => (await unwrap(api.GET('/v1/assistant/usage'))).data as AssistantUsage, { staleTime: 60_000 });
}

// ---- Answers as text with a little structure ----

export type Inline = { kind: 'text'; text: string } | { kind: 'bold'; text: string } | { kind: 'link'; text: string; href: string; internal: boolean };
export type Block = { kind: 'p'; parts: Inline[] } | { kind: 'ol' | 'ul'; items: Inline[][] };

/** Bold and links in one line. Only app paths and https links become links; anything else stays text. */
export function parseInline(line: string): Inline[] {
  const out: Inline[] = [];
  const re = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let at = 0;
  for (let m = re.exec(line); m; m = re.exec(line)) {
    if (m.index > at) out.push({ kind: 'text', text: line.slice(at, m.index) });
    if (m[1] !== undefined) out.push({ kind: 'bold', text: m[1] });
    else {
      const href = m[3]!;
      const internal = /^\/(?!\/)[^\s]*$/.test(href);
      if (internal || /^https:\/\//.test(href)) out.push({ kind: 'link', text: m[2]!, href, internal });
      else out.push({ kind: 'text', text: m[2]! });
    }
    at = m.index + m[0].length;
  }
  if (at < line.length) out.push({ kind: 'text', text: line.slice(at) });
  return out;
}

/** Paragraphs, numbered lists and bullet lists: what the assistant is asked to write. */
export function parseAnswer(text: string): Block[] {
  const blocks: Block[] = [];
  for (const line of text.split('\n')) {
    const trimmed = line.trim();
    const last = blocks[blocks.length - 1];
    const ol = /^\d+[.)]\s+(.*)$/.exec(trimmed);
    const ul = /^[-*•]\s+(.*)$/.exec(trimmed);
    if (ol || ul) {
      const kind = ol ? 'ol' : 'ul';
      const item = parseInline((ol ?? ul)![1]!);
      if (last && last.kind === kind) last.items.push(item);
      else blocks.push({ kind, items: [item] });
    } else if (trimmed === '') {
      blocks.push({ kind: 'p', parts: [] });
    } else if (last && last.kind === 'p' && last.parts.length > 0) {
      last.parts.push({ kind: 'text', text: ' ' }, ...parseInline(trimmed));
    } else {
      blocks.push({ kind: 'p', parts: parseInline(trimmed) });
    }
  }
  return blocks.filter((b) => b.kind !== 'p' || b.parts.length > 0);
}

/** The transcript's first question, for the handover form's subject. */
export const firstQuestion = (c: Conversation | null) => c?.messages.find((m) => m.role === 'user')?.text ?? '';
